import * as React from 'react';
import { useAppSelector } from 'store/hook';
import { BaseTableList } from 'components/AgGrid/AgGrid';
import { PaddedListWrapper } from 'components/AgGrid/CellRenderer/ListView.styled';
import {
  Rule,
  Status,
  DeleteNotification,
  DeactivateNotification,
  AlertDelivery,
  LastEdited,
  NotificationConditions,
  AppliesTo,
  SeverityDisplay,
  notificationsConditionsValueGetter,
  alertDeliveryValueGetter,
} from './NotificationListCellRenderer';
import type { Role } from 'components/Permission/permission.types';
import { UpdateRuleModal } from './modals/updateRuleModal';
import RuleHandler from 'handlers/rule.handler';
import { DeleteRuleModal } from './modals/DeleteRule';
import { useTranslation } from 'react-i18next';

enum OpenModal {
  None,
  EditRule,
  DeleteRule,
}

interface Props {
  exportTo: boolean;
  handleExport: (val: boolean) => void;
  enableDarkTheme: boolean;
  selectedViewOption: string;
  selectedRuleOption: string;
}

export const NotificationList = ({
  exportTo,
  handleExport,
  enableDarkTheme,
  selectedViewOption,
  selectedRuleOption,
}: Props) => {
  const { t } = useTranslation();
  const unitSystem = useAppSelector((state) => state.persistedReducer).customer
    .unitSystem;
  const userRole = useAppSelector((state) => state.authReducer.role) as Role;
  const mlInsightsAllowedRoles: Role[] = ['director', 'internal-user'];
  const ruleHandler = new RuleHandler();
  const [selectedRuleId, setSelectedRuleId] = React.useState('');
  const { rules } = useAppSelector((state) => state.ruleReducer);
  const filteredRules = React.useMemo(() => {
    return rules.filter((rule) => {
      const viewMatch =
        selectedViewOption === 'active'
          ? rule.isActive
          : selectedViewOption === 'inactive'
          ? !rule.isActive
          : true;

      const typeMatch =
        selectedRuleOption === 'all' || rule?.ruleType === selectedRuleOption;

      // Hide ml-insights rules unless user has director or internal-user role
      const mlInsightsMatch =
        rule?.featureType !== 'ml-insights' ||
        mlInsightsAllowedRoles.includes(userRole);

      return viewMatch && typeMatch && mlInsightsMatch;
    });
  }, [rules, selectedViewOption, selectedRuleOption, userRole]);

  const [modalOpen, setModalOpen] = React.useState(OpenModal.None);
  const [columnDefs] = React.useState([
    {
      field: 'ruleName',
      headerName: t('rule_name'),
      width: 340,
      cellRenderer: Rule,
      cellRendererParams: {
        field: 'ruleId',
        handleRuleNameClick: async (ruleId: string) => {
          setSelectedRuleId(ruleId);
          setModalOpen(OpenModal.EditRule);
        },
      },
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      field: 'bumperNumbers',
      headerName: 'Applies To',
      width: 300,
      cellRenderer: AppliesTo,
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      field: 'conditions',
      headerName: t('condition_s'),
      suppressSizeToFit: true,
      valueGetter: (params: any) =>
        notificationsConditionsValueGetter(params, unitSystem),
      cellRenderer: NotificationConditions,
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      field: 'notificationLevel',
      headerName: t('severity'),
      cellRenderer: SeverityDisplay,
      sortable: true,
    },
    {
      field: 'alertByEmail',
      headerName: t('alert_delivery_method_s'),
      maxWidth: 350,
      cellRenderer: AlertDelivery,
      valueGetter: alertDeliveryValueGetter,
      sortable: true,
    },
    {
      field: 'isActive',
      headerName: t('cdp_step_status'),
      maxWidth: 250,
      cellRenderer: Status,
      sortable: true,
    },
    {
      field: 'updatedAt',
      headerName: t('last_edited'),
      maxWidth: 250,
      cellRenderer: LastEdited,
      sortable: true,
    },
    {
      field: 'ruleId',
      headerName: 'Activate/Deactivate',
      maxWidth: 280,
      cellStyle: { textAlign: 'center' },
      cellRenderer: DeactivateNotification,
      cellRendererParams: {
        field: 'ruleId',
        handleRuleStatusClick: async (ruleId: string) => {
          await ruleHandler.updateRuleStatus(ruleId);
        },
      },
    },
    {
      field: 'ruleId',
      headerName: t('boundary_importer_delete'),
      maxWidth: 280,
      cellStyle: { textAlign: 'center' },
      cellRenderer: DeleteNotification,
      cellRendererParams: {
        field: 'ruleId',
        handleRuleStatusClick: async (ruleId: string) => {
          setSelectedRuleId(ruleId);
          setModalOpen(OpenModal.DeleteRule);
        },
      },
    },
  ]);

  const selectedRule = filteredRules.find(
    (rule) => rule.ruleId === selectedRuleId
  );

  return (
    <>
      {selectedRule && modalOpen === OpenModal.DeleteRule && (
        <DeleteRuleModal
          open={modalOpen === OpenModal.DeleteRule}
          key={`${modalOpen.toString()}-delete-rule-modal}`}
          rule={selectedRule}
          handleAbort={() => {
            setModalOpen(OpenModal.None);
          }}
          enableDarkTheme={enableDarkTheme}
        />
      )}
      {selectedRule && modalOpen === OpenModal.EditRule && (
        <>
          <UpdateRuleModal
            key={`${modalOpen.toString()}-edit-rule-modal`}
            open={modalOpen === OpenModal.EditRule}
            handleAbort={() => {
              setModalOpen(OpenModal.None);
            }}
            title={t('edit_rule')}
            ruleData={selectedRule}
            enableDarkTheme={enableDarkTheme}
          />
        </>
      )}
      <PaddedListWrapper>
        <BaseTableList
          rowData={filteredRules}
          columnDefs={columnDefs}
          exportTo={exportTo}
          handleExport={handleExport}
        />
      </PaddedListWrapper>
    </>
  );
};
